/**
 * Parse CSV row handling quoted values (comma inside quotes does not split).
 * @param {string} line - CSV line string
 * @returns {string[]} Array of column values (unquoted, trimmed)
 */
function parseCSVRow(line) {
  const columns = [];
  let currentCol = '';
  let inQuotes = false;

  for (let j = 0; j < line.length; j++) {
    const char = line[j];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      columns.push(currentCol.trim().replace(/^"|"$/g, ''));
      currentCol = '';
    } else {
      currentCol += char;
    }
  }
  columns.push(currentCol.trim().replace(/^"|"$/g, ''));
  return columns;
}

/**
 * Parse full CSV text into rows of column arrays.
 * Column order: 0=user_id, 1=full_name, 2=email, 3=phone_number, ...
 * @param {string} csvText - Raw CSV string
 * @returns {{ header: string[], rows: string[][] }}
 */
function parseCSV(csvText) {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return { header: [], rows: [] };
  const header = parseCSVRow(lines[0]);
  const rows = lines.slice(1).map((line) => parseCSVRow(line));
  return { header, rows };
}

/**
 * Check if email is valid for export: non-empty, not starting with 'dummy'.
 * @param {string} email
 * @returns {boolean}
 */
function isValidEmailForExport(email) {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (!trimmed) return false;
  if (trimmed.toLowerCase().startsWith('dummy')) return false;
  return true;
}

/**
 * Normalize phone: digits only for length check.
 * @param {string} raw
 * @returns {string} digits only
 */
function phoneDigits(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw.replace(/\D/g, '');
}

/**
 * Escape CSV field (wrap in quotes if contains comma or quote).
 * @param {string} value
 * @returns {string}
 */
function escapeCSV(value) {
  if (value == null) return '""';
  const s = String(value).trim();
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return `"${s}"`;
}

/**
 * Convert backend CSV to Limechat format.
 * Header: countrycode,phonenumber,firstname,lastname (firstname/lastname kept empty).
 * - countrycode: first 2 digits of phone if 12 digits, else 91 for 10-digit India
 * - phonenumber: phone without country code (last 10 if 12 digit)
 * Rows with no phone are skipped.
 * @param {string} csvText - Raw CSV from download endpoint
 * @returns {string} CSV string for download
 */
export function toLimechatCSV(csvText) {
  const { rows } = parseCSV(csvText);
  const header = 'countrycode,phonenumber,firstname,lastname\n';
  const out = [];

  for (const row of rows) {
    const rawPhone = (row[3] || '').trim();
    const digits = phoneDigits(rawPhone);
    if (!digits) continue;

    let countrycode = '';
    let phonenumber = rawPhone;
    if (digits.length === 12) {
      countrycode = digits.slice(0, 2);
      phonenumber = digits.slice(2);
    } else if (digits.length === 10) {
      countrycode = '91';
      phonenumber = digits;
    } else {
      phonenumber = digits;
    }

    // firstname, lastname columns kept empty — do not fetch values
    out.push([countrycode, phonenumber, '', ''].map(escapeCSV).join(','));
  }

  return header + out.join('\n');
}

/**
 * Convert backend CSV to email-only format.
 * Header: email
 * Only rows with valid email (non-empty, not starting with 'dummy').
 * @param {string} csvText - Raw CSV from download endpoint
 * @returns {string} CSV string for download
 */
export function toEmailOnlyCSV(csvText) {
  const { rows } = parseCSV(csvText);
  const header = 'email\n';
  const emails = [];

  for (const row of rows) {
    const email = (row[2] || '').trim();
    if (isValidEmailForExport(email)) {
      emails.push(escapeCSV(email));
    }
  }

  return header + emails.join('\n');
}

/**
 * Extract customer emails from CSV for campaign save API.
 * Same column order: email at index 2. Includes all non-empty emails (dummy allowed for save).
 * @param {string} csvText - Raw CSV from download endpoint
 * @returns {string[]}
 */
export function extractEmailsFromCSV(csvText) {
  const { rows } = parseCSV(csvText);
  const emails = [];
  for (const row of rows) {
    const email = (row[2] || '').trim();
    if (email) emails.push(email);
  }
  return emails;
}
